import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload, UploadFile, App } from 'antd';
import { ArgsProps } from 'antd/es/notification';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import { isEmpty } from 'lodash';
import filesize from 'filesize';

import { UploadRequestOption } from 'rc-upload/lib/interface';

import { getArrayBuffer } from '../../../shared/utils';
import { AttachmentFile } from '../models/send-message-request';

const MAX_UPLOAD_FILE_SIZE = 5242880; // 5mb
const MAX_UPLOAD_FILE_COUNT = 10;

type UploadPhotosProps = {
  fileList: UploadFile<any>[];
  setAttachedPhotos: (photos: AttachmentFile[]) => void;
  setFileList: React.Dispatch<React.SetStateAction<UploadFile<any>[]>>;
  disabled: boolean;
};

const UploadPhotos = ({ fileList, setAttachedPhotos, setFileList, disabled }: UploadPhotosProps) => {
  const { notification } = App.useApp();

  const uploadFile = (options: UploadRequestOption<any>) => {
    options.onSuccess?.(
      {
        name: 'name',
        status: 'done',
        url: 'test',
        thumbUrl: 'test',
      },
      options.file,
    );
  };

  const getArrayFromBytes = (fileBytes: ArrayBuffer, type: string, resolve: (value: AttachmentFile) => void, fileName: string) => {
    const array = Array.from(new Uint8Array(fileBytes));

    resolve({ fileBytes: array, mime: type, fileName: fileName } as AttachmentFile);
  };

  const updateFileList = (infoFileList: UploadFile<any>[]) => {
    if (isEmpty(infoFileList)) {
      setFileList([]);
      return setAttachedPhotos([]);
    }

    const tasks = infoFileList.map((element) => {
      return new Promise<AttachmentFile>((resolve, reject) => {
        try {
          getArrayBuffer(element.originFileObj as RcFile, (fileBytes: ArrayBuffer) => {
            if (!element?.originFileObj) {
              reject('Error load files. Error: No file object');
              return;
            }

            getArrayFromBytes(fileBytes, element.originFileObj.type, resolve, element.originFileObj.name);
          });
        } catch (error: any) {
          reject('Error load files. Error: ' + error);
        }
      });
    });

    Promise.all(tasks).then((values) => {
      setAttachedPhotos(values);
    });

    setFileList(infoFileList);
  };

  const onChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { status, name, uid } = info.file;

    switch (status) {
      case 'done':
        notification.success({
          message: 'Upload success',
          top: 55,
          duration: 10,
          description: `${name} file uploaded successfully.`,
        } as ArgsProps);
        break;

      case 'error':
        notification.error({
          message: 'Upload failed',
          top: 55,
          duration: 10,
          description: `${name} file upload failed.`,
        } as ArgsProps);
        break;
    }

    updateFileList(info.fileList);

    if (info.fileList.length >= MAX_UPLOAD_FILE_COUNT && status === 'done' && info.fileList[info.fileList.length - 1].uid === uid) {
      notification.error({
        message: 'Upload failed',
        top: 55,
        duration: 15,
        description: `Max upload file count is ${MAX_UPLOAD_FILE_COUNT}`,
      } as ArgsProps);
    }
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    if (fileList.length >= MAX_UPLOAD_FILE_COUNT) {
      notification.error({
        message: 'Upload failed',
        top: 55,
        duration: 10,
        description: `Max upload file count is ${MAX_UPLOAD_FILE_COUNT}`,
      } as ArgsProps);
    }

    const isNormalFileSize = file.size < MAX_UPLOAD_FILE_SIZE;

    if (!isNormalFileSize) {
      notification.error({
        message: 'Upload failed',
        top: 55,
        duration: 10,
        description: `${file.name} is bigger than ${filesize(MAX_UPLOAD_FILE_SIZE)}`,
      } as ArgsProps);
    }

    return isNormalFileSize || Upload.LIST_IGNORE;
  };

  return (
    <Upload
      name="file"
      accept="*.*"
      multiple={true}
      customRequest={uploadFile}
      maxCount={MAX_UPLOAD_FILE_COUNT}
      fileList={fileList}
      onChange={onChange}
      beforeUpload={beforeUpload}
      listType="picture"
    >
      <Button className="upload-photo-button" icon={<UploadOutlined />} type="primary" shape="circle" title="Attach files" disabled={disabled} />
    </Upload>
  );
};

export default UploadPhotos;
